import {
  ACCOUNT_FEATURE,
  MODAL,
  normalizeAccountRole,
  selectAccountName,
  selectAvailableUserRoles,
  selectUserAccountRole,
  selectUserId,
  selectUserRolePermissions,
} from '@socialive/common';
import { useFeature } from '@socialive/features';
import { useModal } from '@socialive/modals';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'react-toastify';
import UserRoleSelector from '~/components/UserRoleSelector';
import AccountNav from '../components/AccountNav';
import CustomTable, { UserTableRowRenderer } from '../components/CustomTable';
import EmptyResult from '../components/EmptyResult';
import Search from '../components/Search';
import debounce from '../helpers/debounce';
import filterStringBuilder from '../helpers/filterStringBuilder';

import { getRolesLicensing, selectRolesLicensingId } from '~/store';
import '../style/users.css';

const SEARCH_MIN_LENGTH = 2;
const DEFAULT_FILTER_VALUE = 'ALL';

const Users = ({ checkAuthorization, accountId, APIBaseURL, reAuth }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { canManageUsers } = useSelector(selectUserRolePermissions);
  const userRole = useSelector(selectUserAccountRole);
  const userId = useSelector(selectUserId);
  const accountName = useSelector(selectAccountName);
  const roleList = useSelector(selectAvailableUserRoles);
  const availableRoles = useMemo(() => [DEFAULT_FILTER_VALUE, ...roleList], [roleList]);
  const rolesLicensingModal = useModal({ modalId: MODAL.ROLES_LICENSING, trigger: 'Users' });
  const [, { enabled: isUserRolesEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);
  const rolesLicenseId = useSelector(selectRolesLicensingId);

  const [currentPage, setCurrentPage] = useState(0);
  const [filterValue, setFilterValue] = useState(DEFAULT_FILTER_VALUE);
  const [lastFilterValue, setLastFilterValue] = useState(DEFAULT_FILTER_VALUE);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sortedUsers, setSortedUsers] = useState([]);
  const [sortElement, setSortElement] = useState('user.name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [lastUsersFetchPath, setLastUsersFetchPath] = useState('');
  const [search, setSearch] = useState('');
  const [lastSearchedValue, setLastSearchedValue] = useState('');
  const userTableContainerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  const tableHeaderItems = useMemo(
    () => [
      { sort: 'user.name', displayName: 'NAME' },
      { sort: 'user.email', displayName: 'EMAIL' },
      { sort: 'user.location', displayName: 'LOCATION' },
      { sort: 'role', displayName: 'ROLE' },
      { sort: 'user.createdAt', displayName: 'CREATED' },
    ],
    []
  );

  const defaultParams = useMemo(
    () => ({
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        Authorization: sessionStorage.getItem('authToken'),
      },
    }),
    []
  );

  useEffect(() => {
    checkAuthorization('users/');
  }, [checkAuthorization]);

  useEffect(() => {
    const infiniteScroll = () => {
      const isLastPage = currentPage > totalPages - 1;
      if (!isLastPage && userTableContainerRef.current) {
        const topSpace = document.querySelector('.custom-table')?.getBoundingClientRect()?.top;

        const tableHeight = userTableContainerRef.current.getBoundingClientRect().height;

        if (window.innerHeight - topSpace > tableHeight) {
          setCurrentPage(currentPage + 1);
        }
      }
    };

    const debouncedScroll = debounce(infiniteScroll, 1000);

    window.addEventListener('scroll', debouncedScroll, false);
    return () => {
      window.removeEventListener('scroll', debouncedScroll, false);
    };
  }, [currentPage, totalPages]);

  const unselectAllUsers = () => {
    setSortedUsers((previous) => previous.map((user) => ({ ...user, selected: false })));
    setSelectedUsers([]);
  };

  const getUsersList = useCallback(
    (path, shouldOverride) => {
      setIsLoading(true);
      fetch(`${APIBaseURL + path}&accountId=${accountId}`, defaultParams)
        .then((response) => {
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from getUsersList. Redirecting to login.');
            reAuth(location.pathname, encodeURIComponent(location.search));
            return undefined;
          }
          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Received 403 response from user fetch.');
            toast.error('You do not have permission to perform this action.');
            return undefined;
          }
          return response.json();
        })
        .then((data) => {
          const { content, number: pageNumber } = data;
          if (!content?.length) {
            // when no data comes back on the first page then clear the sortedUsers array as there are no results
            if (pageNumber === 0) {
              setSortedUsers([]);
            }
            setIsLoading(false);
            return;
          }

          if (data?.totalPages === 1) {
            setIsLoading(false);
          }

          if (shouldOverride) {
            // when searching, unselect all previously selected users
            unselectAllUsers();
          }

          const updatedUsers = data.content.map((user) => ({
            ...user,
            role: normalizeAccountRole(user.role),
            selected: false,
          }));
          // If search has changed, just override the users, don't append as it resets the pages and results.
          setSortedUsers((previousUsers) => (shouldOverride ? updatedUsers : [...previousUsers, ...updatedUsers]));
          setTotalPages(data.totalPages);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error fetching users list. ', error);
          setIsLoading(false);
        });
    },
    [APIBaseURL, accountId, defaultParams, location.pathname, location.search, reAuth]
  );

  const debouncedGetUsers = useMemo(
    () => debounce((path, hasSearchChanged) => getUsersList(path, hasSearchChanged), 1000),
    [getUsersList]
  );

  const handleEditRole = useCallback(
    async (userKey, role) => {
      if (!role) return;

      const params = {
        ...defaultParams,
        method: 'PATCH',
        body: JSON.stringify({
          role,
        }),
      };

      try {
        const response = await fetch(`${APIBaseURL}/accountUsers/${userKey}`, params);
        if (response.status === 401) {
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handleEditRole. Redirecting to login.');
          reAuth(location.pathname, encodeURIComponent(location.search));
          return;
        }

        if (response.status === 403) {
          // eslint-disable-next-line no-console
          console.error('Received 403 response from user fetch.');
          toast.error('You do not have permission to perform this action.');
          return;
        }

        setSortedUsers((users) =>
          users.map((user) => {
            if (user.id === parseInt(userKey, 10)) {
              return {
                ...user,
                role,
              };
            }
            return user;
          })
        );

        if (isUserRolesEnabled) {
          await dispatch(getRolesLicensing({ trigger: 'Users@handleEditRole' }));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error editing user role. ', error);
        toast.error('Error editing user role.');
      }
    },
    [APIBaseURL, location, reAuth, defaultParams, dispatch, isUserRolesEnabled]
  );

  const handleEditUser = (user) => {
    history.push(`/user/${user}`);
  };

  const handleFilterChange = (event) => {
    setCurrentPage(0);
    setFilterValue(event.target.value);
  };

  const handleSelectAllUsers = (isChecked) => {
    const newSortedUsers = sortedUsers.map((user) => ({ ...user, selected: isChecked }));

    const newSelectedUsers = isChecked ? sortedUsers.map((user) => user.id.toString()) : [];

    setSortedUsers(newSortedUsers);
    setSelectedUsers(newSelectedUsers);
  };

  const handleUserCreate = () => {
    history.push('/user/new');
  };

  const handleUserSelect = (userKey, isChecked) => {
    const newSortedUsers = sortedUsers.map((user) => {
      if (user.id === userKey) {
        return { ...user, selected: isChecked };
      }
      return user;
    });

    const newSelectedUsers = isChecked
      ? [...selectedUsers, userKey.toString()]
      : selectedUsers.filter((key) => key !== userKey.toString());

    setSortedUsers(newSortedUsers);
    setSelectedUsers(newSelectedUsers);
  };

  const handleUsersSort = (sortBy) => {
    const newSortDirection = sortBy === sortElement && sortDirection === 'asc' ? 'desc' : 'asc';
    /**
     * set loading to true since we are clearing out the sortedUsersArray and we will need to fetch a new list
     * this will prevent a no user found component from rendering and instead show the nice loading wheel
     */
    setIsLoading(true);
    setSortedUsers([]);
    setSelectedUsers([]);
    setCurrentPage(0);
    setSortElement(sortBy);
    setSortDirection(newSortDirection);
  };

  const removeUsers = () => {
    const params = { ...defaultParams, method: 'DELETE' };

    const deletePromises = selectedUsers.map((userKey) =>
      fetch(`${APIBaseURL}/accountUsers/${userKey}`, params)
        .then((response) => {
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.error('Received 401 response from removeUsers. Redirecting to login.');
            reAuth(location.pathname, encodeURIComponent(location.search));
            return undefined;
          }

          if (response.status === 403) {
            // eslint-disable-next-line no-console
            console.error('Received 403 response from user fetch.');
            toast.error('To avoid losing account access, users may not remove themselves.');
            return undefined;
          }
          return userKey;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error removing user account access. ', error);
        })
    );

    Promise.all(deletePromises).then((deletedRawUserKeys) => {
      const deletedUserKeys = deletedRawUserKeys.filter(Boolean);

      // No users where removed, return without doing anything
      if (deletedUserKeys.length === 0) return;

      const toastMessage = deletedUserKeys.length > 1 ? 'Users removed.' : 'User removed.';
      toast.success(toastMessage);
      setIsLoading(true);
      setSortedUsers([]);
      setSelectedUsers([]);

      // clear last fetched path and set current page to 0 to refetch page 1
      setCurrentPage(0);
      setLastUsersFetchPath('');
    });
  };

  const handleUsersRemoveRequest = () => {
    // This step included in case we want a
    // modal warning which does not exist in the spec.
    removeUsers();
  };

  // NOTE: This useEffect must remain above the useEffect that makes the call to get users and set the value
  // for lastUsersFetchPath, because this useEffect resets the lastUsersFetchPath back to '' and would not trigger the
  // useEffect watching the value of lastUsersFetchPath when the removeUsers function is called.
  useEffect(() => {
    if (accountId && accountId !== 'null' && !canManageUsers) {
      history.push('/dashboard');
      return;
    }

    setIsLoading(true);
    setSortedUsers([]);
    setCurrentPage(0);
    setLastUsersFetchPath('');
  }, [accountId, history, canManageUsers]);

  useEffect(() => {
    const isSearching = search.length >= SEARCH_MIN_LENGTH;
    const filterObject = {
      role: filterValue === DEFAULT_FILTER_VALUE ? '' : `${filterValue}`,
      'user.name': isSearching ? `"*${encodeURIComponent(search)}*"` : '',
    };
    let sortByDirection = sortDirection;

    // Some nasty manipulation made necessary by MySQL ordering role by index
    if (sortElement === 'role') {
      sortByDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    const filterString = filterStringBuilder(filterObject);

    const pathParts = [
      `/search/account-users?q=${filterString}`,
      `&sort=${sortElement},${sortByDirection}`,
      `&sort=id,asc&page=${currentPage}`,
    ];

    const newPath = pathParts.join('');
    if (lastUsersFetchPath !== newPath) {
      const hasSearchChanged = search !== lastSearchedValue;
      const hasFilterChanged = filterValue !== lastFilterValue;
      setLastUsersFetchPath(newPath);
      debouncedGetUsers(newPath, hasSearchChanged || hasFilterChanged);
      setLastSearchedValue(search);
      setLastFilterValue(filterValue);
    }
  }, [
    filterValue,
    currentPage,
    sortElement,
    sortDirection,
    lastUsersFetchPath,
    search,
    debouncedGetUsers,
    lastSearchedValue,
    lastFilterValue,
  ]);

  const handleSearchChange = useCallback(
    (e) => {
      setSearch(e.target.value.replace(/"/g, ''));
      setCurrentPage(0);
    },
    [setSearch]
  );

  const plural = selectedUsers.length > 1 ? 's' : '';
  const isAllChecked = sortedUsers.length === selectedUsers.length;
  const isCurrentUserAnAdmin = userRole === 'ADMIN';

  return (
    <div className="users-content content-container">
      <AccountNav />
      <div className="users-header header-container">
        <div>
          <h2 className="users-header-title">{accountName} Users</h2>
          {isUserRolesEnabled && rolesLicenseId && (
            <button className="ml-2 btn btn-outline" type="button" onClick={rolesLicensingModal.open}>
              View Licensing
            </button>
          )}
        </div>
        <div className="users-header-buttons">
          <Search
            label="Name"
            className="users-search-filter"
            value={search}
            onChange={handleSearchChange}
            tipText="Min 2 characters"
          />

          <div className="filter role-filter">
            <span className="byline">Role:&nbsp;&nbsp;</span>
            <UserRoleSelector
              roleList={availableRoles}
              onRoleChanged={handleFilterChange}
              role={filterValue}
              className="filter-select"
            />
          </div>
          {selectedUsers.length === 0 && isCurrentUserAnAdmin && (
            <button type="button" id="UsersCreateBtn" className="btn btn-users-create" onClick={handleUserCreate}>
              Add user
            </button>
          )}
          {selectedUsers.length > 0 && isCurrentUserAnAdmin && (
            <button
              type="button"
              id="usersRemoveBtn"
              className="btn red btn-users-remove"
              onClick={handleUsersRemoveRequest}
            >
              Remove user
              {plural}
            </button>
          )}
        </div>
      </div>
      <div ref={userTableContainerRef}>
        {sortedUsers.length === 0 && !isLoading ? (
          <EmptyResult
            imgSrc="/no-users.png"
            title="No Users Found"
            message={`No users with “${search}” in their name`}
          />
        ) : (
          <CustomTable
            handleSort={handleUsersSort}
            handleSelectAll={handleSelectAllUsers}
            headerItems={tableHeaderItems}
            selectedHeaderItem={sortElement}
            sortDirection={sortDirection}
            checkAll={isAllChecked}
            isLoading={isLoading}
            tableBody={sortedUsers.map((item) => (
              <UserTableRowRenderer
                key={item.id}
                enableAdminOverride={isCurrentUserAnAdmin}
                handleItemClicked={handleEditUser}
                handleSelectItem={handleUserSelect}
                handleEditRole={handleEditRole}
                currentUserId={userId}
                item={item}
              />
            ))}
          />
        )}
      </div>
    </div>
  );
};

export default Users;

Users.propTypes = {
  checkAuthorization: PropTypes.func.isRequired,
  accountId: PropTypes.string.isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  reAuth: PropTypes.func.isRequired,
};
